import { Box, Fade, Typography } from "@mui/material";
import { useEffect, useState } from "react";

interface Props {
  creatingTemplateLoading: boolean;
}

const TemplateCreationLoading = ({ creatingTemplateLoading }: Props) => {
  const [loadingMessageIndex, setLoadingMessageIndex] = useState(0);
  const [messageFadeIn, setMessageFadeIn] = useState(true);

  const loadingMessages = [
    "Crafting your template...",
    "Choosing the best words...",
    "Sprinkling in some magic...",
    "Putting some finishing touches...",
    "Almost there...",
  ];

  useEffect(() => {
    if (!creatingTemplateLoading) return;
    setLoadingMessageIndex(0);
    setMessageFadeIn(true);
    const interval = setInterval(() => {
      setMessageFadeIn(false);
      setTimeout(() => {
        setLoadingMessageIndex((prev) => (prev + 1) % loadingMessages.length);
        setMessageFadeIn(true);
      }, 400);
    }, 2500);
    return () => clearInterval(interval);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [creatingTemplateLoading]);

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        py: 6,
        gap: 3,
      }}
    >
      <Box
        sx={{
          width: 80,
          height: 80,
          borderRadius: "50%",
          bgcolor: "primary.light",
          opacity: 0.15,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          position: "relative",
        }}
      >
        <Box
          sx={{
            position: "absolute",
            inset: 0,
            borderRadius: "50%",
            bgcolor: "primary.main",
            opacity: 0.12,
          }}
        />
        <Box
          component="i"
          className="fa-solid fa-pen"
          sx={{
            fontSize: 32,
            color: "primary.main",
            position: "relative",
            transformOrigin: "bottom right",
            animation: "writing 0.5s ease-in-out infinite alternate",
            "@keyframes writing": {
              "0%": {
                transform: "rotate(-20deg) translateX(-4px)",
              },
              "100%": {
                transform: "rotate(10deg) translateX(4px)",
              },
            },
          }}
        />
      </Box>

      <Fade in={messageFadeIn} timeout={350}>
        <Typography variant="h5" sx={{ fontWeight: 600, textAlign: "center" }}>
          {loadingMessages[loadingMessageIndex]}
        </Typography>
      </Fade>

      <Typography
        sx={{
          textAlign: "center",
          fontSize: 16,
          maxWidth: 380,
          lineHeight: 1.7,
        }}
      >
        Our AI is analyzing your responses and creating a personalized template
        that's optimized for maximum engagement. This should take no more than a
        minute.
      </Typography>

      <Box sx={{ display: "flex", gap: 1, mt: 1 }}>
        {[0, 1, 2].map((i) => (
          <Box
            key={i}
            sx={{
              width: 8,
              height: 8,
              borderRadius: "50%",
              bgcolor: "primary.main",
              animation: "bounce 1.4s infinite ease-in-out",
              animationDelay: `${i * 0.16}s`,
              "@keyframes bounce": {
                "0%, 80%, 100%": { transform: "scale(0.6)", opacity: 0.4 },
                "40%": { transform: "scale(1)", opacity: 1 },
              },
            }}
          />
        ))}
      </Box>
    </Box>
  );
};

export default TemplateCreationLoading;
